import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { Button, Modal } from "react-bootstrap";
import { toast } from "react-hot-toast";
import { isErpApiSuccess, setOpeningProductGroupPool } from "../erpClient";
import { getSessionBranchId } from "../utils/sessionBranch";
import { createOldMetalIdempotencyKey } from "../utils/oldMetalPurchaseErpAdapter";
import { numPool, productGroupOptionDisplayName } from "../utils/productGroupPool";
import { searchProductGroupSelectOptions } from "../utils/productGroupOptions";

const MIN_SEARCH_LEN = 3;
const PG_DROPDOWN_VISIBLE_COUNT = 5;
const PG_DROPDOWN_ITEM_HEIGHT = 35;

const TH_STYLE = {
  fontSize: "0.85rem",
  fontWeight: "600",
  whiteSpace: "nowrap",
  backgroundColor: "var(--theme-primary)",
  color: "#ffffff",
};

const INPUT_STYLE = {
  fontSize: "0.85rem",
  padding: "0.4rem 0.5rem",
  height: "38px",
  backgroundColor: "transparent",
};

const emptyRow = () => ({
  key: `row-${Date.now()}-${Math.random().toString(16).slice(2)}`,
  product_group_id: null,
  product_group_name: "",
  pool_pcs: "",
  pool_nwt: "",
});

function OpeningPoolModal({
  show,
  onHide,
  buttonClass,
  tableClass,
  cardClass,
  buttonTableStyle,
  onSaved,
}) {
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [pgDropdown, setPgDropdown] = useState({
    rowKey: null,
    options: [],
    highlight: 0,
    searchCalled: false,
    loading: false,
  });

  const pgInputRefs = useRef({});
  const pgDropdownRef = useRef(null);
  const pgDropdownItemsRef = useRef([]);
  const cellInputRefs = useRef({});
  const searchRequestIdRef = useRef(0);
  const lastSearchedPrefixRef = useRef("");

  const radius = buttonTableStyle === "square" ? "rounded-0" : "rounded-2";

  useEffect(() => {
    if (!show) return;
    setRows([emptyRow()]);
    lastSearchedPrefixRef.current = "";
    searchRequestIdRef.current = 0;
    setPgDropdown({
      rowKey: null,
      options: [],
      highlight: 0,
      searchCalled: false,
      loading: false,
    });
  }, [show]);

  const resetForm = () => {
    setRows([emptyRow()]);
    lastSearchedPrefixRef.current = "";
    searchRequestIdRef.current = 0;
    setPgDropdown({
      rowKey: null,
      options: [],
      highlight: 0,
      searchCalled: false,
      loading: false,
    });
  };

  const handleClose = () => {
    if (!saving) {
      resetForm();
      onHide();
    }
  };

  const searchProductGroups = useCallback(async (rowKey, searchText) => {
    const trimmed = String(searchText || "").trim();
    if (trimmed.length < MIN_SEARCH_LEN) {
      lastSearchedPrefixRef.current = "";
      setPgDropdown({
        rowKey: null,
        options: [],
        highlight: 0,
        searchCalled: false,
        loading: false,
      });
      return;
    }

    // Open dropdown immediately while searching (same pattern as Sales)
    setPgDropdown((prev) => ({
      rowKey,
      options: prev.rowKey === rowKey ? prev.options : [],
      highlight: 0,
      searchCalled: true,
      loading: true,
    }));

    if (lastSearchedPrefixRef.current === trimmed) {
      setPgDropdown((prev) => ({
        ...prev,
        rowKey,
        searchCalled: true,
        loading: false,
      }));
      return;
    }

    const requestId = ++searchRequestIdRef.current;
    try {
      const opts = await searchProductGroupSelectOptions(trimmed);
      if (requestId !== searchRequestIdRef.current) return;
      lastSearchedPrefixRef.current = trimmed;
      setPgDropdown({
        rowKey,
        options: opts,
        highlight: 0,
        searchCalled: true,
        loading: false,
      });
    } catch (e) {
      if (requestId !== searchRequestIdRef.current) return;
      console.error("Error searching product groups:", e);
      setPgDropdown({
        rowKey,
        options: [],
        highlight: 0,
        searchCalled: true,
        loading: false,
      });
    }
  }, []);

  const selectProductGroup = (rowKey, option, rowIndex) => {
    const gid = Number(option?.value);
    let blocked = false;
    const label = productGroupOptionDisplayName(option) || option?.label || "";
    setRows((prev) => {
      if (
        Number.isFinite(gid) &&
        prev.some((r) => r.key !== rowKey && Number(r.product_group_id) === gid)
      ) {
        blocked = true;
        return prev;
      }
      return prev.map((row) =>
        row.key === rowKey
          ? {
            ...row,
            product_group_id: option?.value ?? null,
            product_group_name: label,
          }
          : row,
      );
    });
    if (blocked) {
      toast.error("This product group is already in the table");
      return;
    }
    setPgDropdown({
      rowKey: null,
      options: [],
      highlight: 0,
      searchCalled: false,
      loading: false,
    });
    setTimeout(() => {
      cellInputRefs.current[`${rowIndex}-pcs`]?.focus();
    }, 0);
  };

  const updateRowField = (key, field, value) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.key !== key) return r;
        const next = { ...r, [field]: value };
        if (field === "product_group_name") {
          next.product_group_id = null;
        }
        return next;
      }),
    );
  };

  const maybeAppendRow = (rowIndex, row) => {
    const hasGroup =
      row.product_group_id ||
      String(row.product_group_name || "").trim().length > 0;
    const nwt = numPool(row.pool_nwt);
    if (!hasGroup || nwt <= 0) return;

    setRows((prev) => {
      if (rowIndex !== prev.length - 1) return prev;
      const newRow = emptyRow();
      setTimeout(() => {
        pgInputRefs.current[newRow.key]?.focus();
      }, 80);
      return [...prev, newRow];
    });
  };

  const handleNwtBlur = (rowIndex, row) => {
    setTimeout(() => {
      maybeAppendRow(rowIndex, row);
    }, 0);
  };

  const removeRow = (key) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.key !== key);
      return next.length > 0 ? next : [emptyRow()];
    });
  };

  const filledRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          Number(r.product_group_id) > 0 &&
          (numPool(r.pool_pcs) > 0 || numPool(r.pool_nwt) > 0),
      ),
    [rows],
  );

  const totals = useMemo(() => {
    let pool_pcs = 0;
    let pool_nwt = 0;
    filledRows.forEach((row) => {
      pool_pcs += numPool(row.pool_pcs);
      pool_nwt += numPool(row.pool_nwt);
    });
    return { pool_pcs, pool_nwt };
  }, [filledRows]);

  const handleSave = async () => {
    const branch_id = getSessionBranchId();
    if (branch_id == null) {
      toast.error("Branch not set in session");
      return;
    }
    if (filledRows.length === 0) {
      toast.error("Add at least one product group with Pcs and/or weight");
      return;
    }

    const items = filledRows.map((r) => ({
      product_group_id: Number(r.product_group_id),
      pool_pcs: numPool(r.pool_pcs),
      pool_nwt: numPool(r.pool_nwt),
      idempotency_key: `opening_pg_${r.product_group_id}_v1`,
    }));

    setSaving(true);
    try {
      const idempotencyKey = createOldMetalIdempotencyKey();
      const res = await setOpeningProductGroupPool(
        { branch_id, items },
        { idempotencyKey },
      );
      if (isErpApiSuccess(res)) {
        toast.success("Opening pool saved");
        resetForm();
        onHide();
        onSaved?.();
      } else {
        toast.error(res?.message || "Failed to save opening pool");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to save opening pool");
    } finally {
      setSaving(false);
    }
  };

  const renderPgDropdown = () => {
    if (!pgDropdown.rowKey || !pgDropdown.searchCalled) return null;
    const inputEl = pgInputRefs.current[pgDropdown.rowKey];
    if (!inputEl) return null;
    const rect = inputEl.getBoundingClientRect();
    const portal = document.getElementById("dropdown-portal") || document.body;
    const hasOptions = pgDropdown.options.length > 0;
    const listLen = pgDropdown.loading ? 1 : Math.max(pgDropdown.options.length, 1);
    const visibleItems = Math.min(listLen, PG_DROPDOWN_VISIBLE_COUNT);
    const dropdownHeight = visibleItems * PG_DROPDOWN_ITEM_HEIGHT;
    const dropdownWidth = Math.max(rect.width, 220);

    pgDropdownItemsRef.current = [];

    return ReactDOM.createPortal(
      <div
        ref={pgDropdownRef}
        className="dropdown-menu show shadow-sm"
        style={{
          position: "absolute",
          top: `${rect.bottom + window.scrollY}px`,
          left: `${rect.left + window.scrollX}px`,
          width: `${dropdownWidth}px`,
          maxHeight: `${dropdownHeight}px`,
          height: `${dropdownHeight}px`,
          overflow: "hidden",
          zIndex: 10050,
          border: "1px solid rgba(0,0,0,.15)",
          borderRadius: "0.25rem",
          backgroundColor: "#fff",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            maxHeight: `${dropdownHeight}px`,
            height: `${dropdownHeight}px`,
            overflowY:
              !pgDropdown.loading &&
                hasOptions &&
                pgDropdown.options.length > PG_DROPDOWN_VISIBLE_COUNT
                ? "auto"
                : "hidden",
          }}
        >
          {pgDropdown.loading ? (
            <div
              className="dropdown-item text-muted"
              style={{
                cursor: "default",
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                minHeight: `${PG_DROPDOWN_ITEM_HEIGHT}px`,
              }}
            >
              Searching...
            </div>
          ) : hasOptions ? (
            pgDropdown.options.map((option, idx) => {
              const label = productGroupOptionDisplayName(option) || option.label;
              return (
                <div
                  key={option.value}
                  ref={(el) => {
                    pgDropdownItemsRef.current[idx] = el;
                  }}
                  className="dropdown-item"
                  title={label}
                  style={{
                    cursor: "pointer",
                    padding: "0.4rem 0.75rem",
                    fontSize: "0.85rem",
                    minHeight: `${PG_DROPDOWN_ITEM_HEIGHT}px`,
                    lineHeight: "1.2",
                    backgroundColor:
                      idx === pgDropdown.highlight
                        ? "var(--theme-primary)"
                        : "transparent",
                    color: idx === pgDropdown.highlight ? "#fff" : "inherit",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  onMouseEnter={() =>
                    setPgDropdown((d) => ({ ...d, highlight: idx }))
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    const rowIndex = rows.findIndex(
                      (r) => r.key === pgDropdown.rowKey,
                    );
                    selectProductGroup(pgDropdown.rowKey, option, rowIndex);
                  }}
                >
                  {label}
                </div>
              );
            })
          ) : (
            <div
              className="dropdown-item disabled text-center"
              style={{
                padding: "0.4rem 0.75rem",
                fontSize: "0.85rem",
                minHeight: `${PG_DROPDOWN_ITEM_HEIGHT}px`,
              }}
            >
              No matching items
            </div>
          )}
        </div>
      </div>,
      portal,
    );
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Opening Pool Balance</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div
          className={`table-responsive border ${radius} ${cardClass || ""}`}
          style={{ maxHeight: "calc(38px * 10)", overflowY: "auto" }}
        >
          <table
            className={`table table-bordered table-hover table-sm mb-0 ${tableClass || ""}`}
          >
            <thead
              style={{
                position: "sticky",
                top: 0,
                zIndex: 1,
                backgroundColor: "var(--theme-primary)",
              }}
            >
              <tr>
                <th
                  className="px-2 py-2 text-center"
                  style={{ ...TH_STYLE, width: "40px" }}
                >
                  No.
                </th>
                <th className="px-2 py-2 text-center" style={TH_STYLE}>
                  Product Group 
                </th>
                <th
                  className="px-2 py-2 text-center"
                  style={{ ...TH_STYLE, width: "80px" }}
                >
                  Pcs 
                </th>
                <th
                  className="px-2 py-2 text-center"
                  style={{ ...TH_STYLE, width: "100px" }}
                >
                  N.Wt
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr
                  key={row.key}
                  style={{
                    backgroundColor:
                      rowIndex % 2 === 0 ? "#f8fbff" : "#ffffff",
                  }}
                >
                  <td className="p-0" style={{ width: "40px" }}>
                    <div
                      className="d-flex align-items-center justify-content-center"
                      style={{ height: "38px" }}
                    >
                      <span
                        style={{
                          fontSize: "0.85rem",
                          cursor: "pointer",
                          color: "#6c757d",
                        }}
                        onClick={() => removeRow(row.key)}
                        onMouseEnter={(e) => {
                          e.target.textContent = "×";
                          e.target.style.color = "#dc3545";
                          e.target.style.fontWeight = "600";
                        }}
                        onMouseLeave={(e) => {
                          e.target.textContent = String(rowIndex + 1);
                          e.target.style.color = "#6c757d";
                          e.target.style.fontWeight = "normal";
                        }}
                      >
                        {rowIndex + 1}
                      </span>
                    </div>
                  </td>
                  <td className="p-0 position-relative" style={{ minWidth: 200 }}>
                    <input
                      ref={(el) => {
                        pgInputRefs.current[row.key] = el;
                        cellInputRefs.current[`${rowIndex}-productGroup`] = el;
                      }}
                      type="text"
                      value={row.product_group_name}
                      onChange={(e) => {
                        const val = e.target.value;
                        updateRowField(row.key, "product_group_name", val);
                        void searchProductGroups(row.key, val);
                      }}
                      onClick={() => {
                        if (
                          row.product_group_name &&
                          row.product_group_name.trim().length >= MIN_SEARCH_LEN
                        ) {
                          void searchProductGroups(
                            row.key,
                            row.product_group_name,
                          );
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          !pgDropdown.rowKey ||
                          pgDropdown.rowKey !== row.key
                        ) {
                          return;
                        }
                        if (
                          e.key === "ArrowDown" &&
                          pgDropdown.options.length > 0 &&
                          !pgDropdown.loading
                        ) {
                          e.preventDefault();
                          setPgDropdown((d) => {
                            const next =
                              d.highlight < d.options.length - 1
                                ? d.highlight + 1
                                : 0;
                            setTimeout(() => {
                              pgDropdownItemsRef.current[next]?.scrollIntoView(
                                { block: "nearest" },
                              );
                            }, 0);
                            return { ...d, highlight: next };
                          });
                        } else if (
                          e.key === "ArrowUp" &&
                          pgDropdown.options.length > 0 &&
                          !pgDropdown.loading
                        ) {
                          e.preventDefault();
                          setPgDropdown((d) => {
                            const next =
                              d.highlight > 0
                                ? d.highlight - 1
                                : d.options.length - 1;
                            setTimeout(() => {
                              pgDropdownItemsRef.current[next]?.scrollIntoView(
                                { block: "nearest" },
                              );
                            }, 0);
                            return { ...d, highlight: next };
                          });
                        } else if (
                          e.key === "Enter" &&
                          !pgDropdown.loading &&
                          pgDropdown.options[pgDropdown.highlight]
                        ) {
                          e.preventDefault();
                          selectProductGroup(
                            row.key,
                            pgDropdown.options[pgDropdown.highlight],
                            rowIndex,
                          );
                        } else if (e.key === "Escape") {
                          setPgDropdown({
                            rowKey: null,
                            options: [],
                            highlight: 0,
                            searchCalled: false,
                            loading: false,
                          });
                        }
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setPgDropdown({
                            rowKey: null,
                            options: [],
                            highlight: 0,
                            searchCalled: false,
                            loading: false,
                          });
                        }, 150);
                      }}
                      className={`form-control border-0 ${buttonClass || ""}`}
                      style={INPUT_STYLE}
                      autoComplete="off"
                    />
                  </td>
                  <td className="p-0">
                    <input
                      ref={(el) => {
                        cellInputRefs.current[`${rowIndex}-pcs`] = el;
                      }}
                      type="number"
                      className="form-control border-0 rounded-0 text-end"
                      style={INPUT_STYLE}
                      value={row.pool_pcs}
                      onChange={(e) =>
                        updateRowField(row.key, "pool_pcs", e.target.value)
                      }
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                      }}
                    />
                  </td>
                  <td className="p-0">
                    <input
                      type="number"
                      step="0.001"
                      className="form-control border-0 rounded-0 text-end"
                      style={INPUT_STYLE}
                      value={row.pool_nwt}
                      onChange={(e) =>
                        updateRowField(row.key, "pool_nwt", e.target.value)
                      }
                      onFocus={(e) => {
                        e.target.style.backgroundColor = "#fff";
                      }}
                      onBlur={(e) => {
                        e.target.style.backgroundColor = "transparent";
                        handleNwtBlur(rowIndex, {
                          ...row,
                          pool_nwt: e.target.value,
                        });
                      }}
                    />
                  </td>
                </tr>
              ))}
              <tr style={{ backgroundColor: "#f8f9fa" }}>
                <td className="p-0">
                  <div
                    className="d-flex align-items-center justify-content-center"
                    style={{ height: "38px" }}
                  >
                    <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      Total
                    </span>
                  </div>
                </td>
                <td className="p-0" />
                <td className="p-0" style={{ backgroundColor: "#fff" }}>
                  <div
                    className="d-flex align-items-center justify-content-end px-2"
                    style={{ height: "38px" }}
                  >
                    <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      {totals.pool_pcs}
                    </span>
                  </div>
                </td>
                <td className="p-0" style={{ backgroundColor: "#fff" }}>
                  <div
                    className="d-flex align-items-center justify-content-end px-2"
                    style={{ height: "38px" }}
                  >
                    <span style={{ fontSize: "0.85rem", fontWeight: 600 }}>
                      {totals.pool_nwt.toFixed(3)}
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        {renderPgDropdown()}
      </Modal.Body>
      <Modal.Footer>
        {/* <Button
          variant="secondary"
          className={buttonClass}
          onClick={handleClose}
          disabled={saving}
        >
          Cancel
        </Button> */}
        <Button
          variant="primary"
          className={buttonClass}
          onClick={handleSave}
          disabled={saving || filledRows.length === 0}
        >
          {saving ? "Saving…" : "Save "}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default OpeningPoolModal;
